/**
 * LegalDocumentUpdatedModal
 *
 * "We've updated our <document>" modal, shown when a Remote Config
 * `version<LegalDoc>` key changes. Rendered by `AppOverlayHost` from the
 * `appOverlay` queue head, one document at a time.
 *
 * Flutter sources of truth:
 *  - `lib/services/remote_config_service.dart:234-310` — the seven
 *    `NavigationService.showInfoWidgetDialog(<title>, …, "Close")` calls, one
 *    per `version*` legal key, each awaited inside the update loop (so Flutter
 *    presents them sequentially too — the queue head here is the same
 *    behaviour).
 *  - `lib/presentation/utils/legal_documents_utils.dart:8-73` — the body: a
 *    `RichText` of `'We\'ve updated our '` + the document name as a **tappable
 *    link** (`:18-21`) + `'. By continuing to use the platform you agree to the
 *    updated version.'` (`:66-69`).
 *  - `lib/services/navigation_service.dart:1880-1911` — the dialog itself: a
 *    `CupertinoAlertDialog` in a `CupertinoDialogRoute` (barrier-dismissible by
 *    default) with a single "Close" action that pops it.
 *
 * Web deviations (UI idiom only — `presentation.md` → "Web vs Flutter UI parity
 * scope"; behaviour and the set of controls are unchanged):
 *  - A centred dialog card over a `theme.modalOverlay` scrim instead of
 *    Flutter's `CupertinoAlertDialog`. It is **not** routed through
 *    `InfoSheet` / `SheetService`: `InfoSheet` takes a plain `infoText: string`
 *    and so cannot carry the mid-sentence tappable document link, and its
 *    `screenKey` scoping ties a sheet to one route — wrong for an app-level
 *    Remote Config trigger that can fire on any screen.
 *  - Flutter's barrier dismiss maps to a click on the scrim; Escape is the web
 *    keyboard equivalent of the same affordance. Both do exactly what "Close"
 *    does.
 *  - Title and link label come from the web's existing `l10n.legal.*` names and
 *    the button from `l10n.common.close`, rather than duplicating Flutter's
 *    literals — see `useExpauseLocalizations.getLegalDocumentLabel`
 *    (`presentation/locale/expauseLocalizations.ts`) for why (and for the one
 *    visible consequence: the web says "Terms of Use", Flutter "Terms of
 *    Service").
 *  - **Tapping the document link closes this modal first, then navigates.**
 *    Flutter pushes the legal screen without popping its dialog
 *    (`legal_documents_utils.dart:22-63`), which on a `position: fixed`
 *    top-band web overlay would leave the modal sitting on top of the very page
 *    the user was just sent to. Closing first is the web-idiomatic adjustment;
 *    the document is dismissed from the queue either way, exactly as tapping
 *    "Close" would do.
 */

import React, { useCallback, useEffect } from 'react';
import {
  ExpauseButton,
  ButtonSize,
  ButtonVariant,
} from '@/presentation/components/common/controls/ExpauseButton';
import { APP_OVERLAY_CONSTANTS } from '@/presentation/constants/appOverlayConstants';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useExpauseLocalizations } from '@/presentation/locale/expauseLocalizations';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { VersionUpdatedLegalDocument } from '@/domain/enums/versionUpdatedLegalDocument';
import { getLegalDocumentNavigation } from './legalDocumentUpdatedMappers';

export interface LegalDocumentUpdatedModalProps {
  /**
   * The document whose Remote Config version just changed.
   *
   * Deliberately **not** named `document`: that would shadow the DOM global for
   * the whole component body, which does reach for browser APIs (the `keydown`
   * listener below, `KeyboardEvent`, `React.MouseEvent` targets).
   */
  legalDocument: VersionUpdatedLegalDocument;
  /** Dismiss this document so the next queued one (if any) can present. */
  onClose: () => void;
}

export const LegalDocumentUpdatedModal: React.FC<
  LegalDocumentUpdatedModalProps
> = ({ legalDocument, onClose }) => {
  const { l10n } = useLocale();
  const { expauseLocalizations } = useExpauseLocalizations();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const label = expauseLocalizations.getLegalDocumentLabel(legalDocument);

  // Flutter's `CupertinoDialogRoute` is barrier-dismissible; Escape is the web
  // keyboard equivalent of tapping the barrier.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const onScrimClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const onDocumentLinkClick = useCallback(() => {
    // Close first, then navigate — see the "web deviations" note in the header.
    onClose();
    getLegalDocumentNavigation(legalDocument)();
  }, [legalDocument, onClose]);

  return (
    <div
      onClick={onScrimClick}
      style={{
        position: 'fixed',
        inset: 0,
        // Same band as the blocking overlays: this modal is mounted by
        // `AppOverlayHost` and only ever renders while neither of them is up.
        zIndex: APP_OVERLAY_CONSTANTS.zIndex,
        backgroundColor: theme.modalOverlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: responsive.spacing['3xl'],
        boxSizing: 'border-box',
      }}
    >
      <div
        {...qaAttr({
          id: 'legal-document-updated-modal',
          value: legalDocument,
        })}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={{
          backgroundColor: theme.modalBackground,
          borderRadius: responsive.dialog.borderRadius,
          width: responsive.dialog.width,
          maxWidth: responsive.dialog.maxWidth,
          padding: responsive.spacing['2xl'],
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: responsive.gap.lg,
          textAlign: 'center',
        }}
      >
        <h2
          style={{
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
            margin: 0,
          }}
        >
          {label}
        </h2>

        {/* Flutter's `RichText`: description text + the document name as an
            inline tappable span + the closing sentence
            (`legal_documents_utils.dart:12-71`). */}
        <p
          style={{
            // Flutter `cupertinoAlertDialogDescriptionStyle` — 14 px, muted.
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.regular,
            color: theme.text.secondary,
            margin: 0,
          }}
        >
          {l10n.appOverlay.legalUpdatedPrefix}
          <ExpauseButton
            {...qaAttr({ id: 'legal-document-updated-link' })}
            variant={ButtonVariant.text}
            textColor={theme.blue}
            onClick={onDocumentLinkClick}
            style={{
              // Flutter `cupertinoAlertDialogDescriptionLinkStyle` — same size
              // as the body copy, semibold, highlighted color.
              fontSize: responsive.font.base,
              fontWeight: responsive.fontWeight.semibold,
              fontFamily: 'inherit',
              verticalAlign: 'baseline',
            }}
          >
            {label}
          </ExpauseButton>
          {l10n.appOverlay.legalUpdatedSuffix}
        </p>

        {/* Flutter's single "Close" dialog action
            (`navigation_service.dart:1897-1906`, label passed at
            `remote_config_service.dart:241` etc.). */}
        <ExpauseButton
          {...qaAttr({ id: 'legal-document-updated-close' })}
          variant={ButtonVariant.primary}
          size={ButtonSize.large}
          onClick={onClose}
        >
          {l10n.common.close}
        </ExpauseButton>
      </div>
    </div>
  );
};
